import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDateTimeOrdinal } from './date_util';

const SHIFTS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Unscheduled': return 'Orchid';
    case 'Unconfirmed': return 'Gold';
    case 'Confirmed':
    case 'QR Clocked In':
    case 'QR Complete':
    case 'Clocked In':
    case 'Scheduled': return 'LimeGreen';
    case 'Clocked Out':
    case 'No Show':
    case 'Late':
    case 'Vendor Declined':
    case 'Late/Unconfirmed':
    case 'Cancelled - Stop Service': return 'Crimson';
    case 'Dismissed':
    case 'Complete': return 'LightGreen';
    case 'Cancelled':
    case 'Expired':
    case 'Declined':
    case 'Scheduled but Cancelled': return 'LightGray';
    case 'Extended': return 'Gold';
    case 'Manual Data Needed': return 'Orchid';
    case 'Quote Requested/RFP': return 'Gold';
    default: return 'Gray';
  }
};

export const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftsLoading, setShiftsLoading] = useState(true);
  const [shiftsPage, setShiftsPage] = useState(1);
  const [shiftsSearchTerm, setShiftsSearchTerm] = useState('');

  useEffect(() => {
    fetch(`/apiclientLocation/${id}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        setLocation(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    fetch(`/apilocationShifts/${id}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setShifts(data || []);
        setFilteredShifts(data || []);
        setShiftsLoading(false);
      })
      .catch(() => setShiftsLoading(false));
  }, [id]);

  const formatTime = formatDateTimeOrdinal;

  const filterShifts = (data, term) => {
    if (!term) return data;
    return data.filter(shift => {
      const fields = [
        shift[378]?.value || '',
        shift[321]?.value || '',
        shift[898]?.value || '',
        shift[6] ? formatTime(shift[6].value) : '',
        shift[7] ? formatTime(shift[7].value) : '',
      ];
      return fields.some(val => val.toLowerCase().includes(term));
    });
  };

  const handleShiftsSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setShiftsSearchTerm(term);
    setFilteredShifts(filterShifts(shifts, term));
    setShiftsPage(1);
  };

  if (loading) return <div className="location"><p>Loading...</p></div>;
  if (!location) return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <p>Location not found.</p>
        <button onClick={() => navigate('/locations')} className="mainButton">&larr; Back to Locations</button>
      </div>
    </div>
  );

  const locationCode = location[28] ? location[28].value : '';
  const address = location[8] ? location[8].value : '';
  const phoneNumber = location[15] ? location[15].value : '';
  const timezone = location[102] ? location[102].value : '';

  const sortedFilteredShifts = [...filteredShifts].sort((a, b) => {
    const aStart = a && a[6] && a[6].value ? new Date(a[6].value).getTime() : Infinity;
    const bStart = b && b[6] && b[6].value ? new Date(b[6].value).getTime() : Infinity;
    return aStart - bStart;
  });
  const totalShiftsPages = Math.ceil(sortedFilteredShifts.length / SHIFTS_PER_PAGE);
  const shiftsStart = (shiftsPage - 1) * SHIFTS_PER_PAGE;
  const paginatedShifts = sortedFilteredShifts.slice(shiftsStart, shiftsStart + SHIFTS_PER_PAGE);

  return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <button onClick={() => navigate('/locations')} className="mainButton" style={{ marginBottom: '16px' }}>&larr; Back to Locations</button>
        <h2>Location Details</h2>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Location Code</p><p className="field">{locationCode}</p>
          </div>
          <div style={{ flex: 1 }}>
            <p className="fieldLabel">Timezone</p><p className="field">{timezone}</p>
          </div>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <p className="fieldLabel">Address</p><p className="field">{address}</p>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <p className="fieldLabel">Phone Number</p><p className="field">{phoneNumber}</p>
        </div>

        <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
          Shifts ({shiftsLoading ? '...' : shifts.length})
        </h3>

        {!shiftsLoading && shifts.length > 0 && (
          <div className="searchInputWrapper inputWrapper">
            <input placeholder="Search shifts..." onChange={handleShiftsSearch} />
          </div>
        )}

        {shiftsLoading ? (
          <div className="skeleton-wrapper">
            <table className="skeleton-table">
              <tbody>
                <tr>
                  <th>Client Name</th>
                  <th>Location Code</th>
                  <th>Status</th>
                  <th>Address</th>
                  <th>Armed/Unarmed</th>
                  <th>Guard Type</th>
                  <th>Temporary/Recurring</th>
                  <th>Shift Start (local time)</th>
                  <th>Shift End (local time)</th>
                </tr>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : shifts.length === 0 ? (
          <p style={{ color: '#666', fontSize: '14px' }}>No shifts found for this location.</p>
        ) : filteredShifts.length === 0 ? (
          <p style={{ color: '#666', fontSize: '14px' }}>No shifts match your search.</p>
        ) : (
          <>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
              Showing {shiftsStart + 1}-{Math.min(shiftsStart + SHIFTS_PER_PAGE, filteredShifts.length)} of {filteredShifts.length} shifts
            </p>
            <div className="tableScroll">
            <table id="tableWrapper">
              <tbody>
                <tr>
                  <th>Client Name</th>
                  <th>Location Code</th>
                  <th>Status</th>
                  <th className="addressCell">Address</th>
                  <th>Armed/Unarmed</th>
                  <th>Guard Type</th>
                  <th>Temporary/Recurring</th>
                  <th>Shift Start (local time)</th>
                  <th>Shift End (local time)</th>
                </tr>
                {paginatedShifts.map((shift, idx) => {
                  const statusText = shift[366]?.value || '';
                  const statusColor = getStatusColor(statusText);
                  return (
                    <tr key={idx} onClick={() => navigate(`/shift/${shift[3]?.value}`, { state: { fromLocation: id } })} className="tableRow">
                      <td className="tableCell">{shift[378]?.value || ""}</td>
                      <td className="tableCell">{shift[321]?.value || ""}</td>
                      <td className="tableCell">
                        {statusText && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                            <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }}></span>
                            {statusText}
                          </span>
                        )}
                      </td>
                      <td className="tableCell addressCell">{shift[898]?.value || ""}</td>
                      <td className="tableCell">{shift[107]?.value || ""}</td>
                      <td className="tableCell">{shift[108]?.value || ""}</td>
                      <td className="tableCell">{shift[1295]?.value || ""}</td>
                      <td className="tableCell">{shift[6] ? formatTime(shift[6].value) : ""}</td>
                      <td className="tableCell">{shift[7] ? formatTime(shift[7].value) : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            {totalShiftsPages > 1 && (
              <div className="pagination-wrapper">
                <button
                  className={`pagination-btn ${shiftsPage === 1 ? 'pagination-btn--disabled' : ''}`}
                  onClick={() => setShiftsPage(p => Math.max(1, p - 1))}
                  disabled={shiftsPage === 1}
                >
                  Prev
                </button>
                {getPageNumbers(shiftsPage, totalShiftsPages).map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`pagination-btn ${shiftsPage === page ? 'pagination-btn--active' : ''}`}
                      onClick={() => setShiftsPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
                <button
                  className={`pagination-btn ${shiftsPage === totalShiftsPages ? 'pagination-btn--disabled' : ''}`}
                  onClick={() => setShiftsPage(p => Math.min(totalShiftsPages, p + 1))}
                  disabled={shiftsPage === totalShiftsPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
